import { trackingDb as db } from "../../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { TrackingSession, LocationPoint, TrackingSessionFilters } from "../../types/model/trackingSessionTypes";

const SESSIONS_COLLECTION = "tracking_sessions";
const LOCATION_COLLECTION = "location_tracking";

const toISOString = (value: any): string | undefined => {
  if (!value) return undefined;
  if (typeof value === "string") return value;
  if (typeof value === "number") return new Date(value).toISOString();
  if (value instanceof Date) return value.toISOString();
  if (typeof value.toDate === "function") return (value.toDate() as Date).toISOString();
  // Plain Firestore Timestamp shape: { seconds, nanoseconds }
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000).toISOString();
  return undefined;
};

const toNumber = (value: any): number | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

export const TrackingSessionService = {
  async listSessions(filters: TrackingSessionFilters): Promise<TrackingSession[]> {
    console.log("[Firestore] listSessions — no userAccountId, falling back to date-only query", { filters });
    const sessionsRef = collection(db, SESSIONS_COLLECTION);

    const constraints: any[] = [
      where("startedAt", ">=", new Date(filters.startDate).toISOString().split("T")[0]),
      where("startedAt", "<=", filters.endDate + "T23:59:59.999Z"),
    ];

    const snapshot = await getDocs(query(sessionsRef, ...constraints));
    console.log("[Firestore] listSessions — got", snapshot.size, "docs");

    let results: TrackingSession[] = snapshot.docs.map((docSnapshot) => {
      const raw = docSnapshot.data();
      return {
        sessionId: docSnapshot.id,
        punch_uuid: raw.punch_uuid ?? docSnapshot.id,
        employeeId: raw.employeeId ?? raw.os_emp_account_id ?? "",
        os_emp_account_id: raw.os_emp_account_id ?? raw.employeeId ?? "",
        user_account_id: raw.user_account_id,
        raw_id: raw.raw_id,
        site_id: raw.site_id,
        startedAt: raw.startedAt,
        endedAt: raw.endedAt,
        reason: raw.reason,
        status: raw.status,
        stop_reason: raw.stop_reason,
        created_at: toISOString(raw.created_at),
        updated_at: toISOString(raw.updated_at),
        device_platform: raw.device_platform,
      } as TrackingSession;
    });

    if (filters.employeeId) {
      results = results.filter(
        (s) => s.employeeId === filters.employeeId || s.os_emp_account_id === filters.employeeId
      );
    }

    if (filters.status) {
      results = results.filter((s) => s.status === filters.status);
    }

    if (filters.siteId) {
      results = results.filter((s) => s.site_id === filters.siteId);
    }

    if (filters.searchQuery) {
      const q = filters.searchQuery.toLowerCase();
      results = results.filter(
        (s) =>
          s.employeeId.toLowerCase().includes(q) ||
          s.os_emp_account_id.toLowerCase().includes(q) ||
          (s.site_id ?? "").toLowerCase().includes(q)
      );
    }

    return results;
  },

  async getSessionById(sessionId: string): Promise<TrackingSession | null> {
    const docRef = doc(db, SESSIONS_COLLECTION, sessionId);
    const docSnapshot = await getDoc(docRef);

    if (!docSnapshot.exists()) return null;

    const raw = docSnapshot.data();
    return {
      sessionId: docSnapshot.id,
      punch_uuid: raw.punch_uuid ?? docSnapshot.id,
      employeeId: raw.employeeId ?? raw.os_emp_account_id ?? "",
      os_emp_account_id: raw.os_emp_account_id ?? raw.employeeId ?? "",
      user_account_id: raw.user_account_id,
      raw_id: raw.raw_id,
      site_id: raw.site_id,
      startedAt: raw.startedAt,
      endedAt: raw.endedAt,
      reason: raw.reason,
      status: raw.status,
      stop_reason: raw.stop_reason,
      created_at: toISOString(raw.created_at),
      updated_at: toISOString(raw.updated_at),
      device_platform: raw.device_platform,
    } as TrackingSession;
  },

  async listSessionsByUserAccountId(
    userAccountId: string,
    filters: TrackingSessionFilters
  ): Promise<TrackingSession[]> {
    console.log("[Firestore] listSessionsByUserAccountId", {
      userAccountId,
      startDate: new Date(filters.startDate).toISOString().split("T")[0],
      endDate: filters.endDate + "T23:59:59.999Z",
    });
    const sessionsRef = collection(db, SESSIONS_COLLECTION);

    const snapshot = await getDocs(
      query(
        sessionsRef,
        where("user_account_id", "==", userAccountId),
        where("startedAt", ">=", new Date(filters.startDate).toISOString().split("T")[0]),
        where("startedAt", "<=", filters.endDate + "T23:59:59.999Z")
      )
    );
    console.log("[Firestore] listSessionsByUserAccountId — got", snapshot.size, "docs");

    let results: TrackingSession[] = snapshot.docs.map((docSnapshot) => {
      const raw = docSnapshot.data();
      return {
        sessionId: docSnapshot.id,
        punch_uuid: raw.punch_uuid ?? docSnapshot.id,
        employeeId: raw.employeeId ?? raw.os_emp_account_id ?? "",
        os_emp_account_id: raw.os_emp_account_id ?? raw.employeeId ?? "",
        user_account_id: raw.user_account_id,
        raw_id: raw.raw_id,
        site_id: raw.site_id,
        startedAt: raw.startedAt,
        endedAt: raw.endedAt,
        reason: raw.reason,
        status: raw.status,
        stop_reason: raw.stop_reason,
        created_at: toISOString(raw.created_at),
        updated_at: toISOString(raw.updated_at),
        device_platform: raw.device_platform,
      } as TrackingSession;
    });

    if (filters.employeeId) {
      results = results.filter(
        (s) => s.employeeId === filters.employeeId || s.os_emp_account_id === filters.employeeId
      );
    }
    if (filters.status) {
      results = results.filter((s) => s.status === filters.status);
    }
    if (filters.siteId) {
      results = results.filter((s) => s.site_id === filters.siteId);
    }
    if (filters.searchQuery) {
      const q = filters.searchQuery.toLowerCase();
      results = results.filter(
        (s) =>
          s.employeeId.toLowerCase().includes(q) ||
          s.os_emp_account_id.toLowerCase().includes(q) ||
          (s.site_id ?? "").toLowerCase().includes(q)
      );
    }

    return results;
  },

  async listLocationPoints(sessionId: string): Promise<LocationPoint[]> {
    const locationRef = collection(db, LOCATION_COLLECTION);
    let snapshot = await getDocs(query(locationRef, where("punch_uuid", "==", sessionId)));

    if (snapshot.empty) {
      snapshot = await getDocs(query(locationRef, where("session_id", "==", sessionId)));
    }

    return snapshot.docs.map((docSnapshot) => {
      const raw = docSnapshot.data();
      return {
        id: docSnapshot.id,
        latitude: toNumber(raw.latitude),
        longitude: toNumber(raw.longitude),
        accuracy: toNumber(raw.accuracy),
        altitude: toNumber(raw.altitude),
        speed: toNumber(raw.speed),
        bearing: toNumber(raw.bearing),
        timestamp: toNumber(raw.timestamp) ?? 0,
        recorded_at: raw.recorded_at,
        recorded_at_local: raw.recorded_at_local,
        employee_id: raw.employee_id,
        session_id: raw.session_id,
        tracking_reason: raw.tracking_reason,
        punch_uuid: raw.punch_uuid,
        raw_id: raw.raw_id,
        os_emp_account_id: raw.os_emp_account_id,
        user_account_id: raw.user_account_id,
        site_id: raw.site_id,
        synced_at: toISOString(raw.synced_at),
        device_platform: raw.device_platform,
      } as LocationPoint;
    }).sort((a, b) => a.timestamp - b.timestamp);
  },
};
